import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AdminLoginPage from './pages/admin/LoginPage';
import AdminRegisterPage from './pages/admin/RegisterPage';
import AdminDashboardPage from './pages/admin/DashboardPage';
import AdminEmployeesPage from './pages/admin/EmployeesPage';
import AdminSchedulePage from './pages/admin/SchedulePage';
import LocationManager from './pages/admin/LocationManager';
import EmployeeSchedulePage from './pages/employee/SchedulePage';
import './App.css';

interface PrivateRouteProps {
  children: React.ReactNode;
  isAdmin?: boolean;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, isAdmin = false }) => {
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [isAdminUser, setIsAdminUser] = React.useState(false);

  React.useEffect(() => {
    // Check if auth is initialized by verifying token
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('currentUser');
    
    if (token && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setIsAuthenticated(true);
        setIsAdminUser(userData.role === 'admin');
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    setIsInitialized(true);
  }, []);

  if (!isInitialized) {
    return <div>Loading...</div>;
  }
  
  // If not authenticated at all, redirect to appropriate login
  if (!isAuthenticated) {
    return <Navigate to={isAdmin ? '/admin/login' : '/login'} />;
  }

  // If trying to access admin route without admin rights
  if (isAdmin && !isAdminUser) {
    return <Navigate to='/admin/login' />;
  }

  // If authenticated and has correct permissions
  return <>{children}</>;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/register" element={<AdminRegisterPage />} />
          <Route
            path="/schedule"
            element={
              <PrivateRoute>
                <EmployeeSchedulePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <PrivateRoute isAdmin={true}>
                <AdminDashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/employees"
            element={
              <PrivateRoute isAdmin={true}>
                <AdminEmployeesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/schedule"
            element={
              <PrivateRoute isAdmin={true}>
                <AdminSchedulePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/locations"
            element={
              <PrivateRoute isAdmin={true}>
                <LocationManager />
              </PrivateRoute>
            }
          />
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="/dashboard" element={<Navigate to="/schedule" />} />
          <Route path="/admin" element={<Navigate to="/admin/login" />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
